// src/pages/Markets.jsx
// TradingView Advanced Chart + Economic Calendar
// Properly mounts TradingView widget with correct container/script timing
import React, { useEffect, useRef, useState } from 'react';
import { TrendingUp, Calendar, Maximize2, Minimize2 } from 'lucide-react';

const G = '#00e5a0';

export default function Markets() {
  const [tab,        setTab]        = useState('chart');
  const [fullscreen, setFullscreen] = useState(false);

  return (
    <div className="fade-in" style={{ padding: fullscreen ? '8px 16px' : '20px 24px', maxWidth: '100%', margin: '0 auto' }}>
      {!fullscreen && (
        <div style={{ marginBottom: 20 }}>
          <h1 style={{ fontSize: 22, fontWeight: 600, color: '#f0f0f5', margin: 0, fontFamily: "'IBM Plex Sans',sans-serif" }}>Markets</h1>
          <p style={{ color: '#55556a', fontSize: 12, margin: '3px 0 0', fontFamily: "'DM Mono',monospace" }}>
            Advanced charting · Economic calendar
          </p>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        {[['chart', TrendingUp, 'Advanced Chart'], ['calendar', Calendar, 'Economic Calendar']].map(([id, Icon, label]) => (
          <button key={id} onClick={() => setTab(id)} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 16px', borderRadius: 8, border: '1px solid',
            fontSize: 13, cursor: 'pointer', outline: 'none',
            fontFamily: "'IBM Plex Sans',sans-serif",
            background: tab === id ? 'rgba(0,229,160,0.1)' : 'transparent',
            borderColor: tab === id ? 'rgba(0,229,160,0.3)' : '#1e1e2e',
            color: tab === id ? G : '#8888a8',
            fontWeight: tab === id ? 600 : 400,
          }}>
            <Icon size={13}/> {label}
          </button>
        ))}
        <button onClick={() => setFullscreen(f => !f)} style={{
          marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6,
          background: 'transparent', border: '1px solid #1e1e2e', borderRadius: 8,
          padding: '7px 12px', cursor: 'pointer', color: '#8888a8', fontSize: 12,
          fontFamily: "'IBM Plex Sans',sans-serif",
        }}>
          {fullscreen ? <><Minimize2 size={13}/> Compact</> : <><Maximize2 size={13}/> Full Screen</>}
        </button>
      </div>

      {tab === 'chart' && !fullscreen && (
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', padding: '9px 16px', background: 'rgba(0,229,160,0.03)', border: '1px solid rgba(0,229,160,0.08)', borderRadius: 9, marginBottom: 14 }}>
          {['Search any symbol on the chart', 'Full drawing tools — Fibonacci, trendlines, channels', 'All timeframes: 1m to Monthly', 'All indicators: MA, RSI, MACD, Bollinger and more'].map(tip => (
            <div key={tip} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#8888a8' }}>
              <span style={{ color: G, fontSize: 10 }}>✓</span> {tip}
            </div>
          ))}
        </div>
      )}

      {/* Use key to force full unmount/remount when tab changes */}
      {tab === 'chart'    && <ChartView key="chart" fullscreen={fullscreen} />}
      {tab === 'calendar' && <CalendarView key="calendar" fullscreen={fullscreen} />}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─── ChartView ──────────────────────────────────────────────────────
function ChartView({ fullscreen }) {
  const containerRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // Clear any existing content
    el.innerHTML = '';

    // Build the structure TradingView expects
    const widgetContainer = document.createElement('div');
    widgetContainer.className = 'tradingview-widget-container';
    widgetContainer.style.cssText = 'width:100%;height:100%;';

    const widget = document.createElement('div');
    widget.className = 'tradingview-widget-container__widget';
    widget.style.cssText = 'width:100%;height:calc(100% - 32px);';

    const copyright = document.createElement('div');
    copyright.className = 'tradingview-widget-copyright';
    copyright.style.cssText = 'height:32px;line-height:32px;font-size:11px;color:#55556a;text-align:center;font-family:"DM Mono",monospace;';
    copyright.innerHTML = '<a href="https://www.tradingview.com/" rel="noopener nofollow" target="_blank" style="color:#55556a;text-decoration:none;">Track all markets on TradingView</a>';

    widgetContainer.appendChild(widget);
    widgetContainer.appendChild(copyright);
    el.appendChild(widgetContainer);

    // Build script element with config
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
    script.async = true;
    script.onload  = () => setLoaded(true);
    script.onerror = () => setLoaded(true);

    script.text = JSON.stringify({
      autosize: true,
      symbol: 'OANDA:XAUUSD',
      interval: '60',
      timezone: 'Etc/UTC',
      theme: 'dark',
      style: '1',
      locale: 'en',
      backgroundColor: '#0a0a0f',
      gridColor: 'rgba(30,30,46,0.6)',
      allow_symbol_change: true,
      save_image: true,
      withdateranges: true,
      hide_side_toolbar: false,
      details: true,
      hotlist: true,
      calendar: false,
      show_popup_button: true,
      popup_width: '1000',
      popup_height: '650',
      support_host: 'https://www.tradingview.com',
    });

    widgetContainer.appendChild(script);

    // Fallback timer in case onload doesn't fire
    const timer = setTimeout(() => setLoaded(true), 2500);

    return () => {
      clearTimeout(timer);
      if (el) el.innerHTML = '';
    };
  }, [fullscreen]);

  const h = fullscreen ? 'calc(100vh - 110px)' : 'min(70vh, 640px)';

  return (
    <div style={{ background: '#16161f', border: '1px solid #1e1e2e', borderRadius: 14, overflow: 'hidden', height: h, position: 'relative' }}>
      {!loaded && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12, zIndex: 10, background: '#16161f' }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', border: '2px solid #1e1e2e', borderTopColor: G, animation: 'spin 0.8s linear infinite' }} />
          <div style={{ fontSize: 12, color: '#55556a', fontFamily: "'DM Mono',monospace" }}>Loading chart...</div>
        </div>
      )}
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
    </div>
  );
}

// ─── CalendarView ───────────────────────────────────────────────────
function CalendarView({ fullscreen }) {
  const containerRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.innerHTML = '';

    const widgetContainer = document.createElement('div');
    widgetContainer.className = 'tradingview-widget-container';
    widgetContainer.style.cssText = 'width:100%;height:100%;';

    const widget = document.createElement('div');
    widget.className = 'tradingview-widget-container__widget';
    widget.style.cssText = 'width:100%;height:100%;';

    widgetContainer.appendChild(widget);
    el.appendChild(widgetContainer);

    const script = document.createElement('script');
    script.type   = 'text/javascript';
    script.src    = 'https://s3.tradingview.com/external-embedding/embed-widget-events.js';
    script.async  = true;
    script.onload = () => setLoaded(true);

    script.text = JSON.stringify({
      colorTheme: 'dark',
      isTransparent: true,
      width: '100%',
      height: '100%',
      locale: 'en',
      importanceFilter: '-1,0,1',
      countryFilter: 'us,eu,gb,au,ca,jp,ch,cn',
    });

    widgetContainer.appendChild(script);
    const timer = setTimeout(() => setLoaded(true), 2500);

    return () => {
      clearTimeout(timer);
      if (el) el.innerHTML = '';
    };
  }, [fullscreen]);

  const h = fullscreen ? 'calc(100vh - 110px)' : 'min(70vh, 640px)';

  return (
    <div style={{ background: '#16161f', border: '1px solid #1e1e2e', borderRadius: 14, overflow: 'hidden', height: h, position: 'relative' }}>
      {!loaded && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12, zIndex: 10, background: '#16161f' }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', border: '2px solid #1e1e2e', borderTopColor: G, animation: 'spin 0.8s linear infinite' }} />
          <div style={{ fontSize: 12, color: '#55556a', fontFamily: "'DM Mono',monospace" }}>Loading calendar...</div>
        </div>
      )}
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
    </div>
  );
}
